import React, { useState, useEffect, useCallback } from 'react';
import Slider from '../components/home/Slider';
import Banner from '../components/home/Banner';
import ProductTabs from '../components/home/ProductTabs';
import StaticBanners from '../components/home/StaticBanners';
import CategoryProductSection from '../components/home/CategoryProductSection';
import StaticHomeSection from '../components/home/StaticHomeSection';
import TrendingProducts from '../components/home/TrendingProducts';
import storeApi from '../services/storeApi';

/**
 * HomePage - Main homepage component
 * Follows React Rules: Side effects in useEffect, state management
 * Composed of smaller, reusable components
 */
const HomePage = () => {
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState({
    newArrival: [],
    bestseller: [],
    featured: [],
    trending: [],
    sale: [],
    new: []
  });
  const [categoryProducts, setCategoryProducts] = useState({
    laptop: [],
    tvAudio: [],
    bestSellers: []
  });
  const [slides, setSlides] = useState([]);
  const [error, setError] = useState(null);

  // Fetch featured products for slider and tabs
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(true);
        
        // Fetch featured products for tabs
        const [newArrivals, bestsellers, featured, saleProducts] = await Promise.all([
          storeApi.getFeaturedProducts('new_arrival'),
          storeApi.getFeaturedProducts('bestseller'),
          storeApi.getFeaturedProducts('featured'),
          storeApi.getFeaturedProducts('sale')
        ]);

        // Get all products for category sections
        const allProducts = await storeApi.getProducts({ page_size: 20 });

        setProducts({
          newArrival: newArrivals?.results || [],
          bestseller: bestsellers?.results || [],
          featured: featured?.results || [],
          trending: featured?.results?.slice(0, 6) || [],
          sale: saleProducts?.results || [],
          new: newArrivals?.results?.slice(0, 6) || []
        });

        // Set category products (using all products for now, can be filtered by category later)
        const productList = allProducts?.results || [];
        setCategoryProducts({
          laptop: productList.slice(0, 6),
          tvAudio: productList.slice(6, 12),
          bestSellers: bestsellers?.results?.slice(0, 6) || []
        });

        // Create slides from featured products (first 3)
        if (featured?.results && featured.results.length > 0) {
          const slideProducts = featured.results.slice(0, 3);
          const newSlides = slideProducts.map((product, index) => ({
            id: product.id,
            subtitle: product.discount_percentage > 0 
              ? `Sale Offer <span>-${product.discount_percentage}% Off</span> This Week`
              : 'Special Offer This Week',
            title: product.name,
            price: `Starting at <span>PKR ${parseFloat(product.price).toFixed(2)}</span>`,
            link: `/store/product/${product.slug || product.id}`,
            linkText: 'View Product',
            animationStyle: `animation-style-0${(index % 3) + 1}`,
            bgClass: `bg-${(index % 3) + 1}`
          }));
          setSlides(newSlides);
        } else {
          // Fallback slides if no products
          setSlides([
            {
              id: 1,
              subtitle: 'Sale Offer <span>-20% Off</span> This Week',
              title: 'Featured Products',
              price: 'Starting at <span>PKR 0.00</span>',
              animationStyle: 'animation-style-01',
              bgClass: 'bg-1'
            },
            {
              id: 2,
              subtitle: 'Sale Offer <span>Black Friday</span> This Week',
              title: 'Premium Collection',
              price: 'Starting at <span>PKR 824.00</span>',
              animationStyle: 'animation-style-02',
              bgClass: 'bg-2'
            },
            {
              id: 3,
              subtitle: 'Sale Offer <span>-10% Off</span> This Week',
              title: 'New Arrivals',
              price: 'Starting at <span>PKR 1,849.00</span>',
              animationStyle: 'animation-style-01',
              bgClass: 'bg-3'
            }
          ]);
        }
        
        // Clear error if request succeeded (even with empty results)
        setError(null);
      } catch (err) {
        console.error('Error fetching home data:', err);
        // Only show error for real API/network errors (not empty results)
        const status = err.response?.status;
        if (status && status >= 400 && status !== 404) {
          setError('Failed to load products. Please try again later.');
        } else {
          // For 404 or network errors, treat as no products (don't show error)
          setError(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  // Callbacks for side effects - following React Rules
  const handleAddToCart = useCallback(async (productId) => {
    try {
      await storeApi.addToCart(productId, 1);
      // Trigger cart update event for header
      window.dispatchEvent(new Event('cartUpdated'));
      alert('Product added to cart!');
    } catch (error) {
      console.error('Error adding to cart:', error);
      if (error.response?.status === 401) {
        alert('Please login to add items to cart');
      } else {
        alert('Failed to add product to cart. Please try again.');
      }
    }
  }, []);

  const handleAddToWishlist = useCallback(async (productId) => {
    try {
      await storeApi.addToWishlist(productId);
      alert('Product added to wishlist!');
    } catch (error) {
      console.error('Error adding to wishlist:', error);
      if (error.response?.status === 401) {
        alert('Please login to add items to wishlist');
      } else {
        alert('Failed to add product to wishlist. Please try again.');
      }
    }
  }, []);

  const handleQuickView = useCallback((productId) => {
    // Side effect: Show quick view modal
    console.log('Quick view:', productId);
  }, []);

  if (loading) {
    return (
      <div className="product-area pt-60 pb-50">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <p>Loading products...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if we have any products at all
  const hasAnyProducts = 
    (products.newArrival && products.newArrival.length > 0) ||
    (products.bestseller && products.bestseller.length > 0) ||
    (products.featured && products.featured.length > 0) ||
    (products.sale && products.sale.length > 0) ||
    (categoryProducts.laptop && categoryProducts.laptop.length > 0) ||
    (categoryProducts.tvAudio && categoryProducts.tvAudio.length > 0) ||
    (categoryProducts.bestSellers && categoryProducts.bestSellers.length > 0);

  if (error) {
    return (
      <div className="product-area pt-60 pb-50">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <p className="text-danger">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasAnyProducts && !loading) {
    return (
      <div className="product-area pt-60 pb-50">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center">
              <p>There are no products yet. Please check back later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Slider With Banner Area */}
      <div className="slider-with-banner">
        <div className="container">
          <div className="row">
            <div className="col-lg-8 col-md-8">
              <Slider slides={slides} />
            </div>
            <div className="col-lg-4 col-md-4 text-center pt-xs-30">
              <Banner 
                image="banner/1_1.jpg" 
                className=""
              />
              <Banner 
                image="banner/1_2.jpg" 
                className="mt-15 mt-sm-30 mt-xs-30"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Product Tabs */}
      <ProductTabs
        products={products}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
        onQuickView={handleQuickView}
      />

      {/* Static Banner Area */}
      <StaticBanners />

      {/* Laptop Product Area */}
      <CategoryProductSection
        title="Laptop"
        subCategories={[
          { name: 'Prime Video', link: '/store/shop' },
          { name: 'Computers', link: '/store/shop' },
          { name: 'Electronics', link: '/store/shop' }
        ]}
        products={categoryProducts.laptop}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
        onQuickView={handleQuickView}
        className="pt-60 pb-45"
      />

      {/* TV & Audio Product Area */}
      <CategoryProductSection
        title="TV & Audio"
        subCategories={[
          { name: 'Chamcham', link: '/store/shop' },
          { name: 'Sanai', link: '/store/shop' },
          { name: 'Meito', link: '/store/shop' }
        ]}
        products={categoryProducts.tvAudio}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
        onQuickView={handleQuickView}
        className="li-tv-audio-product pb-45"
      />

      {/* Static Home Section */}
      <StaticHomeSection />

      {/* Trending Products */}
      <TrendingProducts
        products={{
          trending: products.trending,
          sale: products.sale,
          new: products.new
        }}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
        onQuickView={handleQuickView}
      />

      {/* Best Sellers Area */}
      <CategoryProductSection
        title="Best Sellers"
        products={categoryProducts.bestSellers}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleAddToWishlist}
        onQuickView={handleQuickView}
        className="li-trendding-products best-sellers pb-45"
      />
    </>
  );
};

export default HomePage;
